/** Benchmark-only PBR surfaces. Original geometry uses a few free CC0 scans;
 * provenance and hashes live beside the source textures. No sim dependency. */
import type { Scene } from '@babylonjs/core/scene';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { HDRCubeTexture } from '@babylonjs/core/Materials/Textures/hdrCubeTexture';
import { themeAssetPath } from './themes';

type Rgb = readonly [number, number, number];
export type AdventureSurface = 'plaster' | 'paving' | 'wood' | 'metal' | 'fabric' | 'leaf' | 'asphalt';
type LoadState = { pending: Set<string>; failed: Set<string> };
export class AdventureMaterials {
  readonly materials = new Map<string, PBRMaterial>();
  readonly textures = new Map<string, Texture>();
  readonly environment: HDRCubeTexture;
  private disposed = false;
  constructor(private readonly scene: Scene, private readonly state: LoadState) {
    const url = this.url('benchmark/afternoon');
    state.pending.add(url);
    this.environment = new HDRCubeTexture(url, scene, 64, false, true, false, false,
      () => { if (!this.disposed) { state.pending.delete(url); state.failed.delete(url); } }, () => { if (!this.disposed) { state.pending.delete(url); state.failed.add(url); } });
  }
  private url(key: string): string {
    const path = themeAssetPath('neighborhood', 'textures', key);
    if (!path) throw new Error(`Missing Adventure benchmark texture: ${key}`);
    return `${import.meta.env.BASE_URL}${path}`;
  }
  private texture(key: string, linear: boolean, scale: number): Texture {
    const id = `${key}:${scale}`;
    const cached = this.textures.get(id);
    if (cached) return cached;
    const url = this.url(key);
    this.state.pending.add(url);
    const texture = new Texture(url, this.scene, false, false, Texture.TRILINEAR_SAMPLINGMODE,
      () => { if (!this.disposed) { this.state.pending.delete(url); this.state.failed.delete(url); } }, () => {
        if (this.disposed) return;
        this.state.pending.delete(url);
        this.state.failed.add(url);
        for (const m of this.materials.values()) {
          if (m.albedoTexture === texture) m.albedoTexture = null;
          if (m.bumpTexture === texture) m.bumpTexture = null;
          if (m.metallicTexture === texture) m.metallicTexture = null;
        }
      });
    texture.gammaSpace = !linear;
    texture.uScale = scale; texture.vScale = scale;
    texture.anisotropicFilteringLevel = 8;
    this.textures.set(id, texture);
    return texture;
  }
  material = (name: string, color: Rgb, surface?: AdventureSurface): PBRMaterial => {
    const key = `${name}:${color.join(',')}:${surface}`;
    const cached = this.materials.get(key);
    if (cached) return cached;
    const m = new PBRMaterial(`adventureBenchmark_${name}`, this.scene);
    m.albedoColor = Color3.FromArray([...color]);
    m.metallic = surface === 'metal' ? .65 : 0;
    m.roughness = surface === 'metal' ? .48 : surface === 'leaf' ? .58 : .88;
    m.environmentIntensity = surface === 'metal' ? 1 : .85;
    const source = surface === 'asphalt' ? 'asphalt' : surface === 'plaster' ? 'plaster' : surface === 'wood' ? 'wood' : null;
    if (source) {
      const scale = surface === 'asphalt' ? .50 : 1;
      const originalFacade = source === 'plaster' && name.startsWith('surface-facade-');
      m.albedoTexture = this.texture(originalFacade ? 'benchmark/original/limewash' : source === 'asphalt' ? 'benchmark/original/asphalt' : `benchmark/${source}/albedo`, false, originalFacade ? .45 : scale);
      m.bumpTexture = this.texture(`benchmark/${source}/normal`, true, scale);
      m.bumpTexture.level = surface === 'plaster' ? .35 : 1.1;
      m.invertNormalMapY = true;
      m.metallicTexture = this.texture(`benchmark/${source}/arm`, true, scale);
      m.useRoughnessFromMetallicTextureAlpha = false;
      m.useRoughnessFromMetallicTextureGreen = true;
      m.useMetallnessFromMetallicTextureBlue = true;
      m.useAmbientOcclusionFromMetallicTextureRed = true;
      m.ambientTextureStrength = .65;
    }
    if (surface === 'leaf') {
      m.albedoTexture = this.texture('benchmark/original/leaf', false, 1);
      m.albedoTexture.hasAlpha = true;
      m.albedoTexture.wrapU = Texture.CLAMP_ADDRESSMODE;
      m.albedoTexture.wrapV = Texture.CLAMP_ADDRESSMODE;
      m.useAlphaFromAlbedoTexture = true;
      m.transparencyMode = PBRMaterial.PBRMATERIAL_ALPHATEST;
      m.alphaCutOff = .45;
      m.backFaceCulling = false;
      m.twoSidedLighting = true;
    }
    this.materials.set(key, m);
    return m;
  };
  dispose(): void {
    this.disposed = true;
    for (const m of this.materials.values()) m.dispose(false, false);
    for (const t of this.textures.values()) { if (t.url) this.state.pending.delete(t.url); t.dispose(); }
    if (this.environment.url) this.state.pending.delete(this.environment.url);
    if (this.scene.environmentTexture === this.environment) this.scene.environmentTexture = null;
    this.environment.dispose();
    this.materials.clear(); this.textures.clear();
  }
}
